import { useState, useEffect, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { Loader2, Upload, X, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { useCreateAgreementPayment } from './useAgreementPayments';
import {
  ALLOWED_PAYMENT_FILE_TYPES,
  MAX_PAYMENT_FILE_SIZE_BYTES,
  MAX_PAYMENT_FILE_SIZE_LABEL,
} from './types';

function parseBrl(s: string): number {
  const cleaned = s
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agreementId: string;
  agreementName: string;
}

export function PaymentModal({ open, onOpenChange, agreementId, agreementName }: Props) {
  const { accounts, categories, costCenters } = useFinance();
  const create = useCreateAgreementPayment();
  const fileRef = useRef<HTMLInputElement>(null);

  const today = format(new Date(), 'yyyy-MM-dd');
  const currentMonth = format(new Date(), 'yyyy-MM');

  const [paymentDate, setPaymentDate] = useState(today);
  const [referenceMonth, setReferenceMonth] = useState(currentMonth);
  const [grossStr, setGrossStr] = useState('');
  const [glosaStr, setGlosaStr] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [costCenterId, setCostCenterId] = useState<string>('none');
  const [registerAsTransaction, setRegisterAsTransaction] = useState(true);
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const incomeCategories = useMemo(
    () => categories.filter((c) => c.type === 'income'),
    [categories],
  );
  const activeAccounts = useMemo(() => accounts.filter((a) => a.isActive), [accounts]);
  const activeCostCenters = useMemo(() => costCenters.filter((cc) => cc.isActive), [costCenters]);

  const grossAmount = parseBrl(grossStr);
  const glosaAmount = parseBrl(glosaStr);
  const netAmount = Math.max(0, grossAmount - glosaAmount);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setPaymentDate(today);
    setReferenceMonth(currentMonth);
    setGrossStr('');
    setGlosaStr('');
    setCategoryId('none');
    setCostCenterId('none');
    setRegisterAsTransaction(true);
    setNotes('');
    setFile(null);
    setFileError(null);
    if (fileRef.current) fileRef.current.value = '';
    setAccountId(activeAccounts[0]?.id ?? '');
  }, [open, activeAccounts, today, currentMonth]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleFile = (selected: File | null) => {
    setFileError(null);
    if (!selected) {
      setFile(null);
      return;
    }
    if (selected.type && !ALLOWED_PAYMENT_FILE_TYPES.includes(selected.type)) {
      setFileError(`Formato não suportado: ${selected.type}`);
      return;
    }
    if (selected.size > MAX_PAYMENT_FILE_SIZE_BYTES) {
      setFileError(`Arquivo excede ${MAX_PAYMENT_FILE_SIZE_LABEL}.`);
      return;
    }
    setFile(selected);
  };

  const valid = grossAmount > 0 && (!registerAsTransaction || !!accountId);

  const handleSubmit = async () => {
    if (!valid) return;
    try {
      await create.mutateAsync({
        agreementId,
        referenceMonth,
        paymentDate,
        grossAmount,
        glosaAmount,
        netAmount,
        accountId: accountId || null,
        categoryId: categoryId === 'none' ? null : categoryId,
        costCenterId: costCenterId === 'none' ? null : costCenterId,
        notes,
        ...(file ? { file } : {}),
        registerAsTransaction,
      });
      onOpenChange(false);
    } catch {
      // toast já mostrou
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Registrar repasse</DialogTitle>
          <p className="text-sm text-muted-foreground">{agreementName}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ref-month">Mês de referência *</Label>
              <Input
                id="ref-month"
                type="month"
                value={referenceMonth}
                onChange={(e) => setReferenceMonth(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Data do recebimento *</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="gross">Valor bruto *</Label>
              <Input
                id="gross"
                placeholder="R$ 0,00"
                value={grossStr}
                onChange={(e) => setGrossStr(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="glosa">Glosa</Label>
              <Input
                id="glosa"
                placeholder="R$ 0,00"
                value={glosaStr}
                onChange={(e) => setGlosaStr(e.target.value)}
              />
            </div>
          </div>

          {grossAmount > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-2 text-center text-sm">
              Líquido: <strong className="tabular-nums">{formatBrl(netAmount)}</strong>
              {glosaAmount > 0 && (
                <span className="ml-2 text-xs text-expense">
                  ({((glosaAmount / grossAmount) * 100).toFixed(1).replace('.', ',')}% glosado)
                </span>
              )}
            </div>
          )}

          {/* Anexo */}
          <div className="space-y-1.5">
            <Label>Anexo (opcional)</Label>
            {file ? (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-2">
                <FileText size={18} className="mt-0.5 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => handleFile(null)}
                  disabled={create.isPending}
                >
                  <X size={14} />
                </Button>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-border bg-secondary/20 px-3 py-3 transition-colors hover:bg-secondary/40">
                <Upload size={16} className="text-muted-foreground" />
                <span className="text-sm">Anexar guia/comprovante</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  PDF, imagem, planilha — até {MAX_PAYMENT_FILE_SIZE_LABEL}
                </span>
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept={ALLOWED_PAYMENT_FILE_TYPES.join(',')}
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
              </label>
            )}
            {fileError && (
              <p className="rounded border border-expense/30 bg-expense/5 p-1.5 text-xs text-expense">
                {fileError}
              </p>
            )}
          </div>

          {/* Lançar como transação */}
          <div className="rounded-lg border border-border p-3">
            <label className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={registerAsTransaction}
                onChange={(e) => setRegisterAsTransaction(e.target.checked)}
                className="h-4 w-4 rounded accent-primary"
              />
              Lançar como receita já recebida
            </label>
            {registerAsTransaction && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Conta *</Label>
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Categoria</Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— sem categoria —</SelectItem>
                      {incomeCategories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Centro de custo</Label>
                  <Select value={costCenterId} onValueChange={setCostCenterId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— sem centro de custo —</SelectItem>
                      {activeCostCenters.map((cc) => (
                        <SelectItem key={cc.id} value={cc.id}>
                          {cc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pay-notes">Observações</Label>
            <Textarea
              id="pay-notes"
              rows={2}
              placeholder="Procedimentos cobertos, divergências, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!valid || create.isPending}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {create.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Registrando…
              </>
            ) : (
              'Registrar repasse'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
